import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fileUrl, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { Heart, MessageCircle, MapPin, GraduationCap, Briefcase, Wallet, Languages, BadgeCheck, ShieldOff, Flag, Sparkles, Sigma } from "lucide-react";
import { toast } from "sonner";

export default function ProfileDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLang();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [interestStatus, setInterestStatus] = useState(null); // null, "pending", "accepted", "rejected"
  const [sending, setSending] = useState(false);
  const [activePhoto, setActivePhoto] = useState(0);
  const [aiScore, setAiScore] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [kundli, setKundli] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/profile/${id}`);
        setProfile(data);
        // Detect existing interest
        const sent = await api.get("/interests/sent");
        const found = sent.data.find((i) => i.to_user_id === id);
        if (found) setInterestStatus(found.status);
        else {
          const recv = await api.get("/interests/received");
          const r = recv.data.find((i) => i.from_user_id === id);
          if (r) setInterestStatus(r.status === "accepted" ? "accepted" : "received");
        }
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Profile not found");
        navigate("/browse");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  const sendInterest = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/interests", { to_user_id: id });
      setInterestStatus(data.status);
      toast.success("Interest sent");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const fetchAiScore = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.get(`/match/ai-score/${id}`);
      setAiScore(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setAiLoading(false); }
  };
  const fetchKundli = async () => {
    try {
      const { data } = await api.get(`/match/kundli/${id}`);
      setKundli(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const blockUser = async () => {
    if (!window.confirm("Block this user? They won't appear in your discovery and can't message you.")) return;
    try { await api.post(`/users/${id}/block`); toast.success("User blocked"); navigate("/browse"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const reportUser = async (reason) => {
    try { await api.post(`/users/${id}/report`, { reason }); toast.success("Reported. Thank you."); setReportOpen(false); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (loading || !profile) {
    return <div className="max-w-7xl mx-auto px-6 py-32 text-center text-[#8C857B]" data-testid="profile-loading">Loading profile…</div>;
  }

  const isSelf = user && user.id === profile.id;
  const photos = profile.photos || [];
  const hero = photos[activePhoto] ? fileUrl(photos[activePhoto]) : null;

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16" data-testid={`profile-detail-${profile.id}`}>
      <Link to="/browse" className="text-sm text-[#8C857B] hover:text-[#C85A44]">← Back to discover</Link>

      <div className="mt-8 grid lg:grid-cols-12 gap-8 md:gap-12">
        {/* Photos */}
        <div className="lg:col-span-7">
          <div className="relative aspect-[4/5] rounded-3xl overflow-hidden bg-[#F2EFE9]">
            {hero ? (
              <img src={hero} alt={profile.full_name} className="absolute inset-0 w-full h-full object-cover" />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-[#8C857B] font-serif-display text-7xl">
                {profile.full_name?.[0]}
              </div>
            )}
          </div>
          {photos.length > 1 && (
            <div className="mt-4 flex gap-3 overflow-x-auto" data-testid="profile-photo-strip">
              {photos.map((p, i) => (
                <button key={p} onClick={() => setActivePhoto(i)}
                  className={`relative h-20 w-20 rounded-xl overflow-hidden flex-shrink-0 ring-2 transition ${i === activePhoto ? "ring-[#C85A44]" : "ring-transparent"}`}>
                  <img src={fileUrl(p)} alt="" className="absolute inset-0 w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Bio */}
        <div className="lg:col-span-5">
          <p className="overline mb-3">{t("pd.profile")}</p>
          <h1 className="font-serif-display text-4xl md:text-5xl leading-tight tracking-tight inline-flex items-center gap-3">
            {profile.full_name}
            {profile.verified && (
              <span data-testid="verified-badge" className="inline-flex items-center gap-1 text-[#2E5C46] text-sm font-sans bg-[#E8F0EA] rounded-full px-3 py-1 align-middle">
                <BadgeCheck className="h-4 w-4" strokeWidth={2} /> {t("pd.verified")}
              </span>
            )}
            {profile.is_premium && (
              <span data-testid="premium-badge" className="inline-flex items-center gap-1 text-[#C85A44] text-xs font-sans bg-[#FAE5E0] rounded-full px-2 py-0.5 align-middle">
                Premium
              </span>
            )}
          </h1>
          <div className="mt-3 text-sm text-[#8C857B] flex flex-wrap items-center gap-3">
            {profile.age && <span>{profile.age} yrs</span>}
            {profile.city && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" strokeWidth={1.5}/>{profile.city}{profile.country ? `, ${profile.country}` : ""}</span>}
            {profile.religion && <><span className="opacity-50">·</span><span>{profile.religion}</span></>}
          </div>

          {!isSelf && (
            <div className="mt-8 flex flex-wrap gap-3 items-center">
              {interestStatus === "accepted" ? (
                <Link to={`/chat/${profile.id}`} data-testid="profile-message-button"
                  className="inline-flex items-center gap-2 rounded-full bg-[#C85A44] hover:bg-[#A84A36] text-white px-6 py-3 text-sm transition">
                  <MessageCircle className="h-4 w-4" strokeWidth={1.5}/> Open chat
                </Link>
              ) : interestStatus === "pending" ? (
                <span data-testid="profile-interest-pending" className="inline-flex items-center gap-2 rounded-full bg-[#F2EFE9] text-[#2C2A28] px-6 py-3 text-sm">
                  Interest sent · awaiting response
                </span>
              ) : interestStatus === "received" ? (
                <Link to="/interests" data-testid="profile-received-cta" className="inline-flex items-center gap-2 rounded-full bg-[#2E5C46] hover:opacity-90 text-white px-6 py-3 text-sm">
                  They sent you an interest — view it
                </Link>
              ) : interestStatus === "rejected" ? (
                <span className="inline-flex items-center gap-2 rounded-full bg-[#F2EFE9] text-[#8C857B] px-6 py-3 text-sm">Not interested at this time</span>
              ) : (
                <button onClick={sendInterest} disabled={sending} data-testid="profile-interest-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-[#C85A44] hover:bg-[#A84A36] disabled:opacity-60 text-white px-6 py-3 text-sm transition">
                  <Heart className="h-4 w-4" strokeWidth={1.5}/> {sending ? t("pd.sending") : t("pd.sendInterest")}
                </button>
              )}
              <button onClick={blockUser} data-testid="profile-block-btn" className="inline-flex items-center gap-1.5 text-xs text-[#8C857B] hover:text-[#9C3A3A] transition">
                <ShieldOff className="h-3.5 w-3.5" strokeWidth={1.5}/> {t("pd.block")}
              </button>
              <button onClick={() => setReportOpen((v) => !v)} data-testid="profile-report-btn" className="inline-flex items-center gap-1.5 text-xs text-[#8C857B] hover:text-[#9C3A3A] transition">
                <Flag className="h-3.5 w-3.5" strokeWidth={1.5}/> {t("pd.report")}
              </button>
            </div>
          )}
          {reportOpen && (
            <div className="mt-4 p-4 rounded-xl bg-[#F2EFE9] border border-[#E2DCD0]" data-testid="report-panel">
              <p className="text-xs text-[#5d574f] mb-2">{t("pd.reportWhy")}</p>
              <div className="flex flex-wrap gap-2">
                {["Fake profile", "Inappropriate content", "Harassment", "Spam"].map((r) => (
                  <button key={r} onClick={() => reportUser(r)} className="text-xs rounded-full bg-white border border-[#E2DCD0] hover:bg-[#C85A44] hover:text-white hover:border-transparent px-3 py-1.5 transition">
                    {r}
                  </button>
                ))}
              </div>
            </div>
          )}

          {!isSelf && (
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              <div className="p-5 rounded-2xl border border-[#E2DCD0] bg-white">
                <div className="flex items-center justify-between mb-3">
                  <div className="overline inline-flex items-center gap-2"><Sparkles className="h-3.5 w-3.5 text-[#C85A44]"/>{t("pd.aiCompat")}</div>
                  {aiScore?.score != null && (
                    <span data-testid="ai-score" className="font-serif-display text-3xl text-[#C85A44]">{aiScore.score}<span className="text-base text-[#8C857B]">/100</span></span>
                  )}
                </div>
                {aiScore ? (
                  <div className="text-sm text-[#3a3733] space-y-2">
                    <p>{aiScore.summary}</p>
                    {aiScore.strengths?.length > 0 && (
                      <ul className="list-disc pl-4 text-xs text-[#5d574f] space-y-1">{aiScore.strengths.map((s,i)=>(<li key={i}>{s}</li>))}</ul>
                    )}
                  </div>
                ) : (
                  <button onClick={fetchAiScore} disabled={aiLoading} data-testid="ai-score-btn"
                    className="w-full rounded-full bg-[#2C2A28] hover:bg-black disabled:opacity-60 text-white px-4 py-2 text-xs transition">
                    {aiLoading ? t("pd.aiAnalysing") : t("pd.aiReveal")}
                  </button>
                )}
              </div>
              <div className="p-5 rounded-2xl border border-[#E2DCD0] bg-white">
                <div className="flex items-center justify-between mb-3">
                  <div className="overline inline-flex items-center gap-2"><Sigma className="h-3.5 w-3.5 text-[#C85A44]"/>{t("pd.kundli")}</div>
                  {kundli?.score != null && (
                    <span data-testid="kundli-score" className="font-serif-display text-3xl text-[#C85A44]">{kundli.score}<span className="text-base text-[#8C857B]">/{kundli.max}</span></span>
                  )}
                </div>
                {kundli ? (
                  <div className="text-sm">
                    <p className="text-[#3a3733] mb-2">{kundli.verdict}</p>
                    {kundli.breakdown && (
                      <div className="text-xs text-[#8C857B] grid grid-cols-2 gap-x-3 gap-y-1">
                        {Object.entries(kundli.breakdown).map(([k,v]) => (
                          <div key={k} className="flex justify-between"><span className="capitalize">{k.replace(/_/g," ")}</span><span className="text-[#2C2A28]">{v}</span></div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <button onClick={fetchKundli} data-testid="kundli-btn"
                    className="w-full rounded-full bg-[#2C2A28] hover:bg-black text-white px-4 py-2 text-xs transition">
                    {t("pd.kundliCheck")}
                  </button>
                )}
              </div>
            </div>
          )}

          {profile.about_me && (
            <div className="mt-10">
              <p className="overline mb-3">{t("pd.about")}</p>
              <p className="leading-relaxed text-[#3a3733]">{profile.about_me}</p>
            </div>
          )}

          <div className="mt-10 grid grid-cols-2 gap-y-6 gap-x-4 border-t border-b border-[#E2DCD0] py-8">
            <Detail icon={<GraduationCap className="h-4 w-4" strokeWidth={1.5}/>} label="Education" value={profile.education} />
            <Detail icon={<Briefcase className="h-4 w-4" strokeWidth={1.5}/>} label="Profession" value={profile.profession} />
            <Detail icon={<Wallet className="h-4 w-4" strokeWidth={1.5}/>} label="Income" value={profile.annual_income} />
            <Detail icon={<Languages className="h-4 w-4" strokeWidth={1.5}/>} label="Mother tongue" value={profile.mother_tongue} />
            <Detail label="Community" value={profile.community} />
            <Detail label="Marital status" value={profile.marital_status} />
            <Detail label="Height" value={profile.height_cm ? `${profile.height_cm} cm` : null} />
            <Detail label="Diet" value={profile.diet} />
          </div>

          {profile.family_about && (
            <div className="mt-8">
              <p className="overline mb-3">{t("pd.family")}</p>
              <p className="leading-relaxed text-[#3a3733]">{profile.family_about}</p>
            </div>
          )}

          {(profile.partner_about || profile.partner_age_min || profile.partner_age_max || profile.partner_religion) && (
            <div className="mt-8">
              <p className="overline mb-3">{t("pd.lookingFor")}</p>
              <p className="leading-relaxed text-[#3a3733]">
                {profile.partner_age_min || profile.partner_age_max ? `Ages ${profile.partner_age_min || "?"}–${profile.partner_age_max || "?"}. ` : ""}
                {profile.partner_religion ? `${profile.partner_religion}. ` : ""}
                {profile.partner_about || ""}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Detail({ icon, label, value }) {
  return (
    <div>
      <div className="overline mb-1 flex items-center gap-2">{icon}{label}</div>
      <div className="text-sm text-[#2C2A28]">{value || <span className="text-[#8C857B]">—</span>}</div>
    </div>
  );
}
